<template>
  <transition name="slide-fade">
    <div
      v-if="visible"
      class="side-panel-overlay"
      @dblclick="closeOnOverlay"
    >
      <div class="side-panel">
        <!-- Header -->
        <div class="side-panel-header">
          <div
            class="d-flex w-100 justify-content-between align-items-start"
          >
            <div
              class="pr-2"
              :class="{ 'w-100': isEditMode }"
            >
              <div class="d-flex w-100 align-items-center">
                <b-form-input
                  v-if="isEditMode"
                  id="task-title"
                  v-model="form.title"
                  :placeholder="$t('Enter task title')"
                  size="lg"
                  required
                  autofocus
                  autocomplete="off"
                />
                <div
                  v-else
                  class="d-flex align-items-center"
                >
                  <h4 class="mb-0 mr-1 panel-title">
                    {{ task ? task.title : $t("New Task") }}
                  </h4>

                  <b-badge
                    v-b-tooltip.hover="linkCopied ? 'Copied!' : 'Copy Share Link'"
                    :variant="linkCopied ? 'success' : 'warning'"
                    class="cursor-pointer"
                    @click="copyShareLink"
                  >
                    <feather-icon icon="LinkIcon" />
                  </b-badge>
                </div>

              </div>
              <span
                v-if="task"
                class="task-id"
              >#{{ task.id || task.uuid?.substring(0, 8) }}</span>
              <span v-if="!isEditMode && task && task.labels?.length">
                <b-badge
                  v-for="label in task.labels"
                  :key="label.uuid"
                  :style="{ backgroundColor: label.color, marginLeft: '8px' }"
                >
                  {{ label.name }}
                </b-badge>
              </span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <b-button
                v-if="task && !isEditMode"
                variant="primary"
                style="width: 100px"
                @click="enterEditMode"
              >
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="mr-50"
                />
                {{ $t("Edit") }}
              </b-button>
              <b-button
                variant="link"
                class="close-btn"
                @click="close"
              >
                <feather-icon
                  icon="XIcon"
                  size="20"
                />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="side-panel-content">
          <b-form @submit.prevent="onSubmit">
            <b-row>
              <!-- Main Content -->
              <b-col
                md="8"
                sm="12"
              >
                <!-- View Mode: Description Display -->
                <div
                  v-if="!isEditMode && task"
                  class="mb-3"
                >
                  <div
                    class="border border-2 rounded-lg p-1"
                    @click="onClick"
                  >
                    <markdown-it-vue
                      class="markdown-body"
                      :options="options"
                      :content="processMentionContent(form.description, GET_CURRENT_BOARD?.members?.data || [])"
                    />
                  </div>
                </div>

                <!-- Edit Mode: Description Editor -->
                <div
                  v-if="isEditMode"
                  class="border border-2 rounded-lg p-1"
                >
                  <b-form-group
                    :label="$t('Description')"
                    label-for="task-description"
                    class="mb-3"
                  >
                    <markdown-editor
                      id="task-description"
                      v-model="form.description"
                      :placeholder="$t('Add description...')"
                      :rows="18"
                      :task-uid="task?.uuid"
                      :board-members="boardMembers"
                    />
                  </b-form-group>
                </div>

                <!-- Comments -->
                <task-comments v-if="!isEditMode" />
              </b-col>

              <!-- Sidebar -->
              <b-col
                md="4"
                sm="12"
              >
                <!-- View Mode: Status, Assignee & Due Date Display -->
                <div v-if="!isEditMode && task">
                  <div class="task-metadata mb-3">
                    <div class="metadata-item">
                      <label class="metadata-label">{{ $t("Assignee") }}</label>
                      <div class="metadata-value">
                        <div
                          v-if="form.assigneeUid"
                          class="assignee-display"
                        >
                          <b-avatar
                            :src="getStorage(getAssignee?.avatar)"
                            :text="getInitials(getAssignee?.name)"
                            size="24"
                            :variant="'light-primary'"
                          />
                          <span class="ml-2">{{ getAssignee?.name }}</span>
                        </div>
                        <span
                          v-else
                          class="text-muted"
                        >{{
                          $t("Unassigned")
                        }}</span>
                      </div>
                    </div>
                    <div class="metadata-item">
                      <label class="metadata-label">{{ $t("Status") }}</label>
                      <div class="metadata-value">
                        <span
                          class="status-badge"
                          :style="{
                            backgroundColor: getStatusColor(form.statusId),
                          }"
                        >
                          {{ getStatusName(form.statusId) }}
                        </span>
                      </div>
                    </div>
                    <div class="metadata-item">
                      <label class="metadata-label">{{ $t("Priority") }}</label>
                      <div class="metadata-value text-capitalize">
                        <div
                          v-if="form.priority"
                          class="d-flex align-items-center"
                        >
                          <feather-icon
                            icon="CircleIcon"
                            stroke-width="5"
                            :class="`text-${priorityOptions.find(p => p.value === form.priority)?.color || 'secondary'}`"
                          />
                          <p class="mb-0 ml-1">
                            {{ form.priority.toLowerCase() }}
                          </p>
                        </div>
                        <span
                          v-else
                          class="text-muted"
                        >{{
                          $t("No priority")
                        }}</span>
                      </div>
                    </div>
                    <div class="metadata-item">
                      <label class="metadata-label">{{ $t("Due Date") }}</label>
                      <div class="metadata-value">
                        <span v-if="form.dueDate">{{
                          moment(form.dueDate).format("MMM D, YYYY")
                        }}</span>
                        <span
                          v-else
                          class="text-muted"
                        >{{
                          $t("No due date")
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="task.milestone"
                    class="task-metadata mb-3"
                  >
                    <div
                      v-b-toggle.task-milestone
                      class="metadata-item justify-content-between cursor-pointer"
                    >
                      <div class="d-flex">
                        <label class="metadata-label">{{
                          $t("Milestone")
                        }}</label>

                        <div class="metadata-value">
                          <span v-if="task.milestone">
                            {{ task.milestone.name }}
                          </span>
                        </div>
                      </div>

                      <feather-icon
                        :icon="
                          collapseShown ? 'ChevronUpIcon' : 'ChevronDownIcon'
                        "
                        size="16"
                        class="ml-1"
                      />
                    </div>

                    <b-collapse
                      id="task-milestone"
                      class="mt-2"
                      @input="collapseShown = $event"
                    >
                      <span>{{ task.milestone.description }}</span>
                      <b-badge
                        :variant="
                          isBeforeTODAY(task.milestone.deadline)
                            ? 'light-danger'
                            : 'light-secondary'
                        "
                        class="d-flex mt-2 mr-50 w-50"
                      >
                        <feather-icon
                          icon="CalendarIcon"
                          size="10"
                          class="mr-25"
                        />
                        {{
                          FORMAT_DATE(task.milestone.deadline, "MMM DD, YYYY")
                        }}
                      </b-badge>
                    </b-collapse>
                  </div>

                  <task-activities :activities="task.activities" />
                </div>

                <!-- Edit Mode: Assignee, Labels, Status & Due Date Fields -->
                <div
                  v-else
                  class="mb-3"
                >
                  <b-form-group
                    :label="$t('Assignee')"
                    label-for="task-assignee"
                    class="mb-2"
                  >
                    <assignee-selector
                      v-model="form.assigneeUid"
                      :members="boardMembers"
                      :multiple="false"
                    />
                  </b-form-group>

                  <b-form-group
                    v-if="isEditMode"
                    :label="$t('Labels')"
                    label-for="task-label"
                    class="mb-2"
                  >
                    <v-select
                      v-model="form.labelUids"
                      :options="GET_CURRENT_BOARD_LABELS"
                      label="name"
                      :reduce="(r) => r.uuid"
                      :clearable="false"
                      :placeholder="$t('Select Labels')"
                      multiple
                    >
                      <template
                        #selected-option-container="{ option, deselect }"
                      >
                        <div
                          class="vs__selected"
                          :style="{ backgroundColor: option.color }"
                        >
                          <span class="text-white font-weight-bold">{{
                            option.name
                          }}</span>
                          <feather-icon
                            icon="XIcon"
                            size="12"
                            class="text-secondary vs__deselect"
                            @click="deselect(option)"
                          />
                        </div>
                      </template>

                      <template #option="{ name, color }">
                        <b-badge :style="{ backgroundColor: color }">
                          {{ name }}
                        </b-badge>
                      </template>
                    </v-select>
                  </b-form-group>

                  <b-form-group
                    :label="$t('Status')"
                    label-for="task-status"
                    class="mb-2"
                  >
                    <v-select
                      v-model="form.statusId"
                      :options="board.statuses"
                      label="name"
                      :reduce="(r) => r.id"
                      :clearable="false"
                    >
                      <template #option="{ name, color, isFinal }">
                        <div
                          class="status-info d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <span
                              class="status-dot"
                              :style="{ backgroundColor: color || '#94a3b8' }"
                            />
                            <span>{{ name }}</span>
                          </div>
                          <feather-icon
                            v-if="isFinal"
                            icon="CheckCircleIcon"
                            size="14"
                            class="mr-2 text-success"
                          />
                        </div>
                      </template>

                      <template #selected-option="{ name, color, isFinal }">
                        <div class="status-info">
                          <span
                            class="status-dot"
                            :style="{ backgroundColor: color || '#94a3b8' }"
                          />
                          <span>{{ name }}</span>
                          <feather-icon
                            v-if="isFinal"
                            icon="CheckCircleIcon"
                            size="14"
                            class="ml-1 text-success"
                          />
                        </div>
                      </template>
                    </v-select>
                  </b-form-group>

                  <b-form-group
                    :label="$t('Priority')"
                    label-for="task-priority"
                    class="mb-2"
                  >
                    <v-select
                      v-model="form.priority"
                      :options="priorityOptions"
                      label="text"
                      :reduce="(r) => r.value"
                      :clearable="false"
                      :placeholder="$t('Select Task Priority')"
                    >
                      <template #selected-option="{ text, description, color }">
                        <div class="d-flex align-items-center">
                          <feather-icon
                            icon="CircleIcon"
                            stroke-width="5"
                            :class="`text-${color}`"
                          />
                          <span class="ml-1 d-flex flex-column">{{ text }}</span>
                        </div>
                      </template>

                      <template #option="{ text, description, color }">
                        <div class="d-flex align-items-center">
                          <feather-icon
                            icon="CircleIcon"
                            stroke-width="5"
                            :class="`text-${color}`"
                          />
                          <div class="ml-1 d-flex flex-column">
                            <span>{{ text }}</span>
                            <small class="text-secondary">{{ description }}</small>
                          </div>
                        </div>
                      </template>
                    </v-select>
                  </b-form-group>

                  <b-form-group
                    :label="$t('Due Date')"
                    label-for="task-due-date"
                    class="mb-2"
                  >
                    <b-form-datepicker
                      id="task-due-date"
                      v-model="form.dueDate"
                      :placeholder="$t('Select Due Date')"
                      :date-format-options="{
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      }"
                      locale="en"
                      reset-button
                    />
                  </b-form-group>

                  <b-form-group
                    :label="$t('Milestone')"
                    label-for="task-milestone"
                    class="mb-2"
                  >
                    <v-select
                      id="task-milestone"
                      v-model="form.milestoneUid"
                      :options="board.milestones?.data || []"
                      label="name"
                      :reduce="(r) => r.uuid"
                      :clearable="true"
                      :placeholder="$t('Select Milestone')"
                    />
                  </b-form-group>
                </div>
              </b-col>
            </b-row>

            <div class="side-panel-actions">
              <div class="d-flex justify-content-between align-items-center">
                <b-button
                  variant="danger"
                  @click="handleCancel"
                >
                  {{ isEditMode ? $t("Cancel") : $t("Close") }}
                </b-button>
                <b-button
                  v-if="isEditMode"
                  type="submit"
                  variant="primary"
                  :disabled="GET_PROCESSING"
                >
                  <b-spinner
                    v-if="GET_PROCESSING"
                    small
                    class="mr-50"
                  />
                  {{ task ? $t("Update Task") : $t("Create Task") }}
                </b-button>
              </div>
            </div>
          </b-form>
        </div>
      </div>

      <CoolLightBox
        :items="fetchImages || []"
        :index="coolLightBoxIndex"
        @close="coolLightBoxIndex = null"
      />
    </div>
  </transition>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormDatepicker,
  BCollapse,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BBadge,
  BAvatar,
  VBToggle,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import MarkdownItVue from 'markdown-it-vue'
import CoolLightBox from 'vue-cool-lightbox'
import { mapGetters } from 'vuex'
import moment from 'moment'
import AssigneeSelector from './AssigneeSelector.vue'
import MarkdownEditor from './MarkdownEditor.vue'
import parseMarkdown, { processMentionContent, fetchImagesFromMarkdown, removeNameFromMentions } from './markdown'
import TaskActivities from './TaskActivities/Index.vue'
import TaskComments from './components/TaskComments.vue'
import { priorityOptions } from '../useProjectManagement'

export default {
  name: 'TaskSidePanel',
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BCollapse,
    BFormDatepicker,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BBadge,
    BAvatar,
    AssigneeSelector,
    MarkdownEditor,
    MarkdownItVue,
    vSelect,
    CoolLightBox,
    TaskActivities,
    TaskComments,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      default: null,
    },
    board: {
      type: Object,
      required: true,
    },
    statusId: {
      type: [String, Number],
      default: null,
    },
    editingMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      moment,
      priorityOptions,
      parseMarkdown,
      removeNameFromMentions,
      processMentionContent,
      form: {
        title: '',
        description: '',
        statusId: null,
        dueDate: null,
        assigneeUid: null,
        labelUids: [],
        priority: 'MEDIUM',
        milestoneUid: null,
      },
      isEditMode: false,
      collapseShown: false,
      options: {
        markdownIt: {
          linkify: true,
          html: true,
        },
        linkAttributes: {
          attrs: {
            target: '_blank',
            rel: 'noopener',
          },
        },
        image: {
          viewer: false,
        },
      },
      coolLightBoxIndex: null,
      uploadingFile: false,
      linkCopied: false,
    }
  },
  computed: {
    ...mapGetters('boards', [
      'GET_CURRENT_BOARD',
      'GET_CURRENT_BOARD_LABELS',
      'GET_PROCESSING',
      'GET_LOADING',
    ]),
    statusOptions() {
      return (this.board?.statuses || []).map(s => ({
        value: s.id,
        text: s.name,
      }))
    },
    boardMembers() {
      return this.board?.members?.data || []
    },
    getAssignee() {
      return this.boardMembers.find(m => m.uuid === this.form.assigneeUid)
    },
    fetchImages() {
      if (this.task) {
        return fetchImagesFromMarkdown(this.task.description)
      }
      return []
    },
  },
  watch: {
    visible(val) {
      if (val) {
        // Set edit mode: true for new task, false for existing task (view mode)
        this.isEditMode = !this.task
        this.initForm()
        // Prevent body scroll when panel is open
        document.body.style.overflow = 'hidden'
      } else {
        // Restore body scroll when panel is closed
        document.body.style.overflow = ''
      }
    },
    task: {
      immediate: true,
      handler() {
        this.initForm()
      },
    },
    editingMode: {
      immediate: true,
      handler(val) {
        this.isEditMode = val
      },
    },
  },
  beforeDestroy() {
    // Ensure body scroll is restored
    document.body.style.overflow = ''
  },
  methods: {
    initForm() {
      if (this.task) {
        this.form = {
          title: this.task.title || '',
          description: this.task.description || '',
          statusId: this.task.status?.id || this.statusId,
          dueDate: this.task.dueDate || null,
          assigneeUid: this.task.assignee?.uuid || null,
          labelUids: this.task.labels?.map(label => label.uuid) || [],
          milestoneUid: this.task.milestone?.uuid || null,
          priority: this.task.priority || 'MEDIUM',
        }
      } else {
        this.form = {
          title: '',
          description: '',
          statusId: this.statusId || this.statusOptions[0]?.value || null,
          dueDate: null,
          assigneeUid: null,
          milestoneUid: this.$route.params.milestoneUid || null,
          labelUids: [],
          priority: 'MEDIUM',
        }
      }
    },
    enterEditMode() {
      this.isEditMode = true
    },
    handleCancel() {
      if (this.isEditMode && this.task) {
        // If editing existing task, go back to view mode
        this.isEditMode = false
        this.initForm() // Reset form to original values
      } else {
        // If creating new task, close panel
        this.close()
      }
    },
    async onSubmit() {
      const payload = {
        title: this.form.title,
        description: removeNameFromMentions(this.form.description),
        statusId: this.form.statusId,
        dueDate: this.form.dueDate,
        projectUid: this.board?.project?.uuid,
        assigneeUid: this.form.assigneeUid,
        labelUids: this.form.labelUids,
        priority: this.form.priority,
        milestoneUid: this.form.milestoneUid,
      }
      this.$emit('submit', payload)
    },
    getStatusName(statusId) {
      return this.board?.statuses?.find(s => s.id === statusId)?.name || ''
    },
    getStatusColor(statusId) {
      return (
        this.board?.statuses?.find(s => s.id === statusId)?.color || '#e1e4e8'
      )
    },
    close() {
      this.$emit('close')
      window.history.pushState(null, '', `/project-management/board/${this.base58Converter(this.board?.uuid)}`)
    },
    closeOnOverlay(e) {
      if (e.target.classList.contains('side-panel-overlay')) {
        this.close()
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    onClick({ target }) {
      if (target.tagName === 'IMG') {
        const src = target.getAttribute('src')
        const index = this.fetchImages.findIndex(img => img === src)

        if (index !== -1) {
          this.coolLightBoxIndex = index
        }
      }
    },
    copyShareLink() {
      const boardUid = this.base58Converter(this.board?.uuid)
      const taskUid = this.base58Converter(this.task?.uuid)

      const shareLink = `${window.location.origin}/project-management/board/${boardUid}/tasks/${taskUid}`
      navigator.clipboard.writeText(shareLink).then(() => {
        this.linkCopied = true
      })
      setTimeout(() => {
        this.linkCopied = false
      }, 2000)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-side-panel.scss";
</style>

<style lang="scss">
.markdown-body img {
  cursor: pointer;
}
</style>
